import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, PenLine, Search } from "lucide-react";
import DrawingsTab from "../../drawings/DrawingsTab";
import { ORIGIN_LABELS, originPath } from "../../drawings/openDrawing";
import { api } from "../lib/api";
import { useRole } from "../context/RoleContext";

/** FT-1 — Drawings is a top-level Field Operations tab: ONE tool, deep-linkable by
 *  ?project= &sheet= &room= &product= &markup=. Every drawing link in the app lands here.
 *  DRW-LINKS — a link carries where it came from (&from=, &vs=, &back=1), so the drawing can
 *  offer a Back bar that puts that view back exactly as it was, and "Open in FRS" returns there
 *  rather than always to Install. */
export default function DrawingsPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { role } = useRole();
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState(false);
  const [chosen, setChosen] = useState("");
  const [drawScopes, setDrawScopes] = useState([]);   // DRW-B §4 — scopes still showing on the plan
  const projectId = chosen || params.get("project") || "";
  const isField = ["foreman", "installer", "warehouse"].includes(String(role || "").toLowerCase());
  const from = params.get("from") || "";
  const vs = params.get("vs") || "";
  // Back is offered only when we took the view's place (a handheld, or the "same tab" preference).
  // Opened in a second tab, the view is still sitting there and a Back bar would be a lie.
  const showBack = params.get("back") === "1" && !!from && !!projectId && !chosen;
  const backTo = (room = "", product = "", scopes = []) => originPath({
    // no origin (opened from the sidebar): the field goes back to Install, the office to FRS
    from: from || (isField ? "install" : "frs"),
    project: projectId, room, product, vs, scopes,
  });

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data.filter((p) => !p.is_archived)))
      .catch(() => setProjects([]));
  }, []);

  const current = useMemo(() => projects.find((p) => p.id === projectId), [projects, projectId]);
  const needle = q.trim().toLowerCase();
  const shown = needle
    ? projects.filter((p) => `${p.job_number || ""} ${p.project_name || ""}`.toLowerCase().includes(needle))
    : projects;
  // Selecting stays in component state: the host shell remounts the field app when the query
  // string changes, which would throw the choice away.
  const open = (id) => { setChosen(id); setPick(false); };

  return (
    <div className="p-2 sm:p-4 space-y-2 fo-scope" data-testid="drawings-page">
      {showBack && (
        <button type="button" onClick={() => navigate(backTo("", "", drawScopes))} data-testid="drawings-back-bar"
                className="sticky top-0 z-40 -mx-2 sm:-mx-4 w-[calc(100%+1rem)] sm:w-[calc(100%+2rem)] flex items-center gap-2
                           min-h-11 px-3 bg-orange-500 text-white text-sm font-semibold shadow">
          <ArrowLeft className="w-4 h-4 shrink-0" />
          Back to {ORIGIN_LABELS[from] || "FRS"}
        </button>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <PenLine className="w-5 h-5 text-orange-500 sm:hidden" />
        <h1 className="text-lg font-bold sm:hidden">Drawings</h1>
        <button type="button" onClick={() => { setQ(""); setPick(true); }} data-testid="drawings-project-select"
                className="h-9 min-w-[14rem] flex items-center gap-1 text-sm border border-gray-300 rounded-lg px-2 bg-white text-left">
          <span className="truncate" data-testid="drawings-project-label">
            {current ? `${current.job_number ? `${current.job_number} · ` : ""}${current.project_name}` : "Select a job…"}
          </span>
          <ChevronDown className="w-4 h-4 ml-auto shrink-0 text-gray-400" />
        </button>
      </div>

      {pick && (
        <div className="fixed inset-0 z-[70] bg-black/30 flex items-start justify-center p-3 sm:pt-24"
             onClick={(e) => { if (e.target === e.currentTarget) setPick(false); }} data-testid="drawings-project-picker">
          <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="flex items-center gap-2 p-2 border-b">
              <Search className="w-4 h-4 text-gray-400" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Job number or job name…"
                     className="flex-1 h-10 text-sm outline-none" data-testid="drawings-project-search" />
            </div>
            <div className="max-h-[60vh] overflow-auto divide-y">
              {shown.map((p) => (
                <button key={p.id} type="button" onClick={() => open(p.id)} data-testid={`drawings-project-${p.id}`}
                        className="w-full text-left px-3 min-h-11 py-2 text-sm hover:bg-orange-50">
                  <span className="font-mono text-xs text-gray-500 mr-2">{p.job_number || "—"}</span>{p.project_name}
                </button>
              ))}
              {shown.length === 0 && <div className="p-4 text-sm text-gray-400">No job matches that.</div>}
            </div>
          </div>
        </div>
      )}

      {!projectId ? (
        <div className="rounded-xl border-2 border-dashed border-gray-300 p-8 text-center text-sm text-gray-600"
             data-testid="drawings-page-empty">
          Pick a job to open its drawings.
        </div>
      ) : (
        <DrawingsTab projectId={projectId} me={localStorage.getItem("fieldops-installer-name") || ""}
                     isField={isField} canWrite
                     link={{ sheet: params.get("sheet") || "", sheetId: params.get("sheet_id") || "", room: params.get("room") || "", scopes: (params.get("scopes") || "").split(",").filter(Boolean),
                             product: params.get("product") || "", markup: params.get("markup") || "" }}
                     onOpenFrs={(room, product, scopes) => navigate(backTo(room, product, scopes))} onScopesChange={setDrawScopes} />
      )}
    </div>
  );
}
